/**
 * Public request check-off page reached by scanning a request's QR label.
 * No auth required — the request `code` acts as the token. The worker taps
 * each line to mark it prepared; state persists to the shared backend.
 */
import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Loader2, CheckSquare, Square, Package, PackageCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_LABEL = { out: "Ut", in: "Inn", return: "Retur" };
const STATUS_LABEL = { draft: "Utkast", preparing: "Klargjøres", ready: "Klar", done: "Levert", cancelled: "Avbrutt" };

export default function LagerRequestPublic() {
  const { code } = useParams();
  const [req, setReq] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/doserne/lager/public/request/${code}`);
      setReq(data);
    } catch (e) {
      setError(e?.response?.status === 404 ? "Forespørselen ble ikke funnet." : "Feil ved lasting.");
    }
    setLoading(false);
  }, [code]);
  useEffect(() => { load(); }, [load]);

  const toggle = async (idx, checked) => {
    // Optimistic update
    setReq((r) => ({ ...r, lines: r.lines.map((l, i) => i === idx ? { ...l, checked } : l) }));
    try { await axios.post(`${API}/doserne/lager/public/request/${code}/toggle-line`, { index: idx, checked }); }
    catch { load(); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>;
  if (error) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><div className="text-center text-slate-500"><Package className="w-12 h-12 mx-auto mb-3 text-slate-300" />{error}</div></div>;

  const total = req.lines.length;
  const done = req.lines.filter((l) => l.checked).length;
  const allDone = total > 0 && done === total;

  return (
    <div className="min-h-screen bg-slate-50 pb-12" data-testid="lager-public-request">
      <div className="bg-slate-900 text-white px-5 pt-8 pb-6">
        <div className="max-w-md mx-auto">
          <div className="text-[10px] uppercase tracking-widest text-cyan-400 font-black">Doserne Utleie AS · Lager</div>
          <div className="flex items-center gap-2 mt-1">
            <h1 className="text-3xl font-black font-mono">{req.code}</h1>
            <span className="text-[10px] uppercase tracking-widest font-black bg-white/10 rounded-full px-2 py-0.5">{TYPE_LABEL[req.type] || req.type}</span>
            <span className="text-[10px] uppercase tracking-widest font-black bg-cyan-500/20 text-cyan-200 rounded-full px-2 py-0.5">{STATUS_LABEL[req.status] || req.status}</span>
          </div>
          {req.project_name && <div className="mt-1 text-sm text-cyan-200 font-semibold">📍 {req.project_name}</div>}
        </div>
      </div>

      <div className="max-w-md mx-auto px-5 -mt-4">
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-4">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-black text-slate-900">Huk av det du har klargjort</span>
            <span className={`text-sm font-black ${allDone ? "text-emerald-600" : "text-slate-500"}`} data-testid="lager-public-progress">{done}/{total}</span>
          </div>
          <div className="h-2 rounded-full bg-slate-100 overflow-hidden mb-4">
            <div className="h-full bg-emerald-500 transition-all" style={{ width: total ? `${(done / total) * 100}%` : "0%" }} />
          </div>
          <div className="space-y-1">
            {req.lines.map((l, idx) => (
              <button
                key={idx}
                onClick={() => toggle(idx, !l.checked)}
                className="w-full text-left flex items-center gap-3 rounded-xl px-2 py-3 active:bg-slate-100 transition"
                data-testid={`lager-public-line-${idx}`}
              >
                {l.checked
                  ? <CheckSquare className="w-6 h-6 text-emerald-600 flex-shrink-0" />
                  : <Square className="w-6 h-6 text-slate-300 flex-shrink-0" />}
                <div className="flex-1 min-w-0">
                  <div className={`text-base font-bold ${l.checked ? "text-slate-400 line-through" : "text-slate-900"}`}>{l.item_name}</div>
                  {l.sku && <div className="text-xs text-slate-400 font-mono">{l.sku}</div>}
                </div>
                <div className="text-lg font-black text-slate-700">{l.quantity} <span className="text-xs text-slate-400">{l.unit}</span></div>
              </button>
            ))}
          </div>
          {allDone && (
            <div className="mt-4 rounded-xl bg-emerald-50 border border-emerald-200 p-3 flex items-center gap-2 text-emerald-700 font-black text-sm" data-testid="lager-public-alldone">
              <PackageCheck className="w-5 h-5" /> Alt klargjort!
            </div>
          )}
        </div>
        {req.note && <div className="mt-3 text-sm text-slate-500 italic px-1">„{req.note}"</div>}
      </div>
    </div>
  );
}
